import type { JSX } from 'react';
import { useMemo } from 'react';
import { Section } from '@/components/blocks/layout';
import { Icon } from '@/components';
import type { RefrigerationUnit } from '@/data/schemas';
import { normalizeThresholdActionKeys } from '@/data/thresholdActions';

interface SafetyChainSummaryProps {
  unit: RefrigerationUnit;
  actionLabelMap?: Map<string, string>;
}

interface ChainColumnProps {
  title: string;
  items: string[];
}

function ChainColumn({ title, items }: ChainColumnProps): JSX.Element {
  return (
    <div className="rounded-lg border border-[var(--border-default)] bg-surface p-4 shadow-sm min-h-[140px]">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
        {title}
      </p>
      <div className="space-y-1.5">
        {items.length > 0 ? (
          items.map((item, i) => (
            <p key={i} className="text-sm font-medium text-text-primary">
              {item}
            </p>
          ))
        ) : (
          <p className="text-sm text-text-tertiary">—</p>
        )}
      </div>
    </div>
  );
}

export function SafetyChainSummary({ unit, actionLabelMap }: SafetyChainSummaryProps): JSX.Element {
  const safety = unit.technical?.safety;

  // Build detection items
  const detectionItems = useMemo(() => {
    const items: string[] = [];
    const toxicCount = safety?.detectors?.toxicCount ?? 0;
    const explosiveCount = safety?.detectors?.explosiveCount ?? 0;
    if (toxicCount > 0) items.push(`${toxicCount} tossici`);
    if (explosiveCount > 0) items.push(`${explosiveCount} esplosivi`);
    const positions = safety?.detectors?.positions ?? [];
    if (positions.length > 0) {
      items.push(positions.slice(0, 3).join(', '));
    }
    return items;
  }, [safety?.detectors]);

  // Build threshold items
  const thresholdItems = useMemo(() => {
    const thresholds = safety?.detectors?.thresholds ?? [];
    return thresholds.slice(0, 4).map((t) => {
      const thresholdUnit = t.unit === 'percent_lel' ? '% LIE' : 'ppm';
      const type = t.thresholdType === 'esplosivita' ? 'Espl' : 'Tox';
      return `${t.value} ${thresholdUnit} (${type})`;
    });
  }, [safety?.detectors?.thresholds]);

  // Build action items (from all thresholds)
  const actionItems = useMemo(() => {
    const thresholds = safety?.detectors?.thresholds ?? [];
    const allActions = new Set<string>();
    thresholds.forEach((t) => {
      const normalized = normalizeThresholdActionKeys(t.actions ?? []);
      normalized.forEach((action) => allActions.add(action));
    });
    // Add scrubber if present
    if (safety?.scrubber?.present) {
      allActions.add('scrubber');
    }
    // Add sgancio if present
    if (safety?.emergencySystems?.sgancioAutomaticoPresent) {
      allActions.add('sgancio');
    }
    // Map to labels
    return Array.from(allActions)
      .slice(0, 5)
      .map((action) => actionLabelMap?.get(action) ?? action);
  }, [
    safety?.detectors?.thresholds,
    safety?.scrubber?.present,
    safety?.emergencySystems?.sgancioAutomaticoPresent,
    actionLabelMap,
  ]);

  // Build discharge items (VS)
  const dischargeItems = useMemo(() => {
    const items: string[] = [];
    const collectorsCount = safety?.vsRelief?.collectorsCount;
    const sizing = safety?.vsRelief?.sizing;
    if (collectorsCount !== undefined) {
      items.push(`${collectorsCount} collettori`);
    }
    if (sizing) {
      items.push(sizing);
    }
    if (safety?.abatementTank?.present) {
      const volume = safety?.abatementTank?.volumeMc;
      items.push(volume ? `Vasca ${volume} m³` : 'Vasca presente');
    }
    return items;
  }, [safety]);

  return (
    <Section spacing="md">
      <div className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr]">
        <ChainColumn title="Sensori" items={detectionItems} />
        <div className="hidden lg:flex items-center px-1">
          <Icon name="arrow_forward" className="text-xl text-text-tertiary" />
        </div>
        <ChainColumn title="Soglie" items={thresholdItems} />
        <div className="hidden lg:flex items-center px-1">
          <Icon name="arrow_forward" className="text-xl text-text-tertiary" />
        </div>
        <ChainColumn title="Azioni" items={actionItems} />
        <div className="hidden lg:flex items-center px-1">
          <Icon name="arrow_forward" className="text-xl text-text-tertiary" />
        </div>
        <ChainColumn title="VS (Ventilazione di Sicurezza)" items={dischargeItems} />
      </div>
    </Section>
  );
}
